import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { MapPin, Phone, Mail, ShieldCheck, Clock, Award } from "lucide-react";
import { fetchPageById, fetchCompanyInfo } from "../lib/cms";
import { COMPANY_INFO } from "../data";
import ContactForm from "../components/ContactForm";

export default function Contact() {
  const [page, setPage] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(COMPANY_INFO);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadContactData() {
      setLoading(true);
      const [pageData, infoData] = await Promise.all([
        fetchPageById("kontakt"),
        fetchCompanyInfo(),
      ]);
      setPage(pageData);
      if (infoData) setCompanyInfo(infoData);
      setLoading(false);
    }
    loadContactData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  const title = page?.title?.da || "Kontakt";
  const contentHtml = page?.content?.da || "";
  const metaTitle = page?.meta_title?.da || "Kontakt - Sehested Tømrerfirma ApS";
  const metaDesc = page?.meta_description?.da || "Har du spørgsmål eller kommentarer? Kontakt os i dag for at høre mere om, hvad vi kan gøre for dig.";

  return (
    <div className="bg-primary-50/30 py-12 md:py-20">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
      </Helmet>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <span className="text-sm font-bold uppercase tracking-widest text-brand-600">Kontakt os</span>
          <h1 className="font-display text-4xl font-bold tracking-tight text-primary-950 sm:text-5xl">
            {title}
          </h1>
          <p className="text-primary-600 text-lg">
            Vi står klar til at rådgive dig og give et uforpligtende tilbud på dit næste projekt.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 items-start">
          {/* Contact Details Column */}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-white rounded-2xl border border-primary-100 p-6 md:p-8 shadow-sm space-y-6">
              <h2 className="font-display text-2xl font-bold text-primary-950">
                Vores informationer
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0 mt-1">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-primary-950 text-sm uppercase tracking-wider">Adresse</h3>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(companyInfo.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-700 hover:text-brand-600 transition-colors text-sm"
                    >
                      {companyInfo.address}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0 mt-1">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-primary-950 text-sm uppercase tracking-wider">Telefon</h3>
                    <a
                      href={`tel:${companyInfo.phoneRaw}`}
                      className="text-primary-700 hover:text-brand-600 transition-colors text-sm"
                    >
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0 mt-1">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-primary-950 text-sm uppercase tracking-wider">E-mail</h3>
                    <a
                      href={`mailto:${companyInfo.email}`}
                      className="text-primary-700 hover:text-brand-600 transition-colors text-sm"
                    >
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
              </div>

              <div className="h-px bg-primary-100 my-6" />

              <div className="text-xs text-primary-500 space-y-1">
                <p>{companyInfo.name || "Sehested Tømrerfirma ApS"}</p>
                <p>CVR-nummer: {companyInfo.cvr}</p>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="bg-white rounded-2xl border border-primary-100 p-6 md:p-8 shadow-sm space-y-4">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-6 w-6 text-brand-600" />
                <h3 className="font-display font-bold text-primary-950">Dækket af Byg Garanti</h3>
              </div>
              <p className="text-sm text-primary-600 leading-relaxed">
                Som medlem af DI Byggeri er vores arbejde dækket af Byg Garanti. Det sikrer dig mod fejl og mangler i op til 10 år efter arbejdets udførelse.
              </p>
              <div className="flex gap-4 pt-2">
                <img
                  src={COMPANY_INFO.bygGarantiLogo}
                  alt="Byg Garanti"
                  className="h-10 object-contain"
                />
                <img
                  src={COMPANY_INFO.diByggeriLogo}
                  alt="DI Byggeri"
                  className="h-10 object-contain"
                />
              </div>
            </div>
          </div>

          {/* Contact Form Column */}
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </div>
    </div>
  );
}
